import { useEffect, useRef, useState } from "react";

import { useHead } from "@unhead/react";
import { Navigate, useParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { NotFoundInAppPage } from "@/components/pages_public/404/NotFound";
import { LIGHTEST_BACKGROUND } from "@/css/constants";
import {
  RoleplaySessionProcessingStatus,
  useRoleplayQuery,
  useRoleplaySessionQuery,
} from "@/graphql";

import { RoleplaySessionContent } from "./RoleplaySessionContent";

const POLLING_INTERVAL = 5000; // 5 seconds
const MAX_POLLING_ATTEMPTS = 120; // 5 minutes total (60 * 5 seconds)

function RoleplaySessionPageContainer() {
  const { sessionUuid, view, roleplaySlug, scenarioSlug } = useParams();
  const pollAttemptsRef = useRef(0);
  const isPollingRef = useRef(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  const { data, loading, refetch } = useRoleplaySessionQuery({
    fetchPolicy: "network-only",
    variables: {
      uuid: sessionUuid || "",
    },
    onCompleted: () => {
      setErrorMsg(undefined);
    },
    onError: () => {
      setErrorMsg("Error fetching roleplay session");
    },
    skip: !sessionUuid,
  });

  const { data: roleplayData } = useRoleplayQuery({
    variables: {
      slug: roleplaySlug,
    },
    skip: !roleplaySlug,
  });

  const roleplay = roleplayData?.roleplay;
  const roleplaySession = data?.roleplaySession;
  const isScenarioBelongToRoleplay =
    roleplay?.scenarios?.some((s) => s?.slug === scenarioSlug) || false;

  const isValidUrl =
    roleplaySession?.scenario?.slug === scenarioSlug &&
    (!roleplaySlug || isScenarioBelongToRoleplay);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const pollData = async () => {
      if (isPollingRef.current) return; // Prevent duplicate polls

      if (
        roleplaySession &&
        roleplaySession.processingStatus !==
          RoleplaySessionProcessingStatus.Processed &&
        pollAttemptsRef.current < MAX_POLLING_ATTEMPTS
      ) {
        isPollingRef.current = true;
        pollAttemptsRef.current += 1;

        try {
          await refetch();
        } catch (error) {
          console.error("Error polling roleplay session:", error);
        } finally {
          isPollingRef.current = false;
        }

        const status =
          roleplaySession?.processingStatus as RoleplaySessionProcessingStatus;

        // Schedule next poll only if conditions are still met
        if (
          roleplaySession &&
          status !== RoleplaySessionProcessingStatus.Processed &&
          pollAttemptsRef.current < MAX_POLLING_ATTEMPTS
        ) {
          timeoutId = setTimeout(pollData, POLLING_INTERVAL);
        }
      }
    };

    // Start polling only if not already polling and conditions are met
    if (
      roleplaySession &&
      roleplaySession.processingStatus !==
        RoleplaySessionProcessingStatus.Processed &&
      !isPollingRef.current
    ) {
      timeoutId = setTimeout(pollData, POLLING_INTERVAL);
    }

    return () => {
      clearTimeout(timeoutId);
      isPollingRef.current = false; // Ensure polling stops on unmount
    };
  }, [roleplaySession, refetch]);

  useHead({
    bodyAttrs: { style: { backgroundColor: LIGHTEST_BACKGROUND } },
  });

  if (!loading && data && !roleplaySession) {
    return <NotFoundInAppPage />;
  }

  if (!loading && !isValidUrl) {
    const correctRoleplaySlug = roleplaySession?.scenario?.slug || "";

    return (
      <Navigate
        to={`/scenarios/${correctRoleplaySlug}/${sessionUuid}${view ? `/${view}` : ""}?`}
      />
    );
  }

  return (
    <>
      <SEOHead
        title={`Session Feedback - ${
          roleplaySession?.scenario?.name || ""
        } - Exec`}
      />
      <Nav pageLoading={loading} />
      <RoleplaySessionContent
        roleplaySession={roleplaySession ?? undefined}
        loading={loading && !data}
        view={view}
        errorMsg={errorMsg}
      />
    </>
  );
}

export function RoleplaySessionPage() {
  return (
    <Authenticate maxWidth="100%" layout="full">
      <RoleplaySessionPageContainer />
    </Authenticate>
  );
}
